import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Heart, Sparkles, CheckCheck, MessageCircle } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function photoUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function relativeTime(iso, t) {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMin = Math.floor((now - then) / 60000);
    if (diffMin < 1) return t("app.notifications.justNow");
    if (diffMin < 60) return t("app.notifications.minutesAgo", { n: diffMin });
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return t("app.notifications.hoursAgo", { n: diffH });
    const diffD = Math.floor(diffH / 24);
    return t("app.notifications.daysAgo", { n: diffD });
  } catch {
    return "";
  }
}

function NotificationRow({ n, onMarkRead, t }) {
  const related = n.related_user;
  const img = photoUrl(related?.main_photo_url);
  const name = related?.display_name || "";
  const isLike = n.type === "like_received";
  const isMatch = n.type === "match_created";
  const isMessage = n.type === "new_message";

  const inner = (
    <div
      className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${!n.read ? "bg-[hsl(var(--nvl-accent-soft,var(--muted)))]" : "hover:bg-[hsl(var(--muted))]"}`}
      style={{ borderColor: "hsl(var(--nvl-border))" }}
      data-testid={`notification-${n.id}`}
      onClick={() => !n.read && onMarkRead && onMarkRead(n.id)}
    >
      <div className="relative">
        <div
          className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center font-display text-lg"
          style={{
            backgroundImage: img ? `url(${img})` : undefined,
            backgroundSize: "cover", backgroundPosition: "center",
            backgroundColor: "hsl(var(--muted))",
            color: "hsl(var(--nvl-accent))",
          }}
        >
          {img ? null : (name || "?").charAt(0).toUpperCase()}
        </div>
        {isMatch || isLike ? (
          <span
            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "hsl(var(--nvl-accent))", color: "hsl(var(--nvl-bg))" }}
          >
            {isLike ? <Heart size={10} /> : <Sparkles size={10} />}
          </span>
        ) : isMessage ? (
          <span
            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "hsl(var(--nvl-ink))", color: "hsl(var(--nvl-bg))" }}
          >
            <MessageCircle size={10} />
          </span>
        ) : null}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${!n.read ? "font-medium" : ""}`}>
          {n.type === "new_message"
            ? t("app.notifications.newMessageFrom", { name })
            : isLike ? t("app.notifications.likeFrom", { name }) : t("app.notifications.matchWith", { name })}
        </p>
        {n.type === "new_message" && n.meta?.preview ? (
          <p className="text-[11px] mt-0.5 truncate" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            "{n.meta.preview}"
          </p>
        ) : null}
        <p className="text-[11px] mt-0.5" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {relativeTime(n.created_at, t)}
        </p>
      </div>
      {!n.read ? (
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: "hsl(var(--nvl-accent))" }}
          aria-label="unread"
        />
      ) : null}
    </div>
  );

  return isLike && n.related_user_id ? (
    <Link to={`/app/discover/${n.related_user_id}`}>{inner}</Link>
  ) : n.related_match_id ? (
    <Link to={n.type === "new_message" ? `/app/messages/${n.related_match_id}` : `/app/matches/${n.related_match_id}`}>{inner}</Link>
  ) : (
    <div>{inner}</div>
  );
}

export default function Notifications() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const markAll = async () => {
    try {
      await api.patch("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="page-notifications">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
            <Bell size={12} className="inline mr-1 -mt-0.5" /> {t("app.notifications.title")}
          </p>
          <h1 className="mt-3 text-4xl md:text-5xl font-display">{t("app.notifications.title")}</h1>
          {unreadCount > 0 ? (
            <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {unreadCount} {t("app.notifications.unreadBadge")}
            </p>
          ) : null}
        </div>
        {unreadCount > 0 ? (
          <button
            onClick={markAll}
            className="nvl-btn nvl-btn-outline"
            data-testid="notif-mark-all-read"
          >
            <CheckCheck size={14} className="mr-2" />
            {t("app.notifications.markAllRead")}
          </button>
        ) : null}
      </div>

      <div className="mt-8 space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse h-20 rounded-lg" style={{ backgroundColor: "hsl(var(--muted))" }} />
          ))
        ) : error ? (
          <div className="nvl-card p-6" data-testid="notif-error">
            <p className="text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="nvl-card p-10 text-center" data-testid="notif-empty">
            <Bell size={22} style={{ color: "hsl(var(--nvl-accent))" }} className="mx-auto" />
            <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.notifications.empty")}
            </p>
          </div>
        ) : (
          items.map((n) => <NotificationRow key={n.id} n={n} onMarkRead={markRead} t={t} />)
        )}
      </div>
    </div>
  );
}
